/* eslint-disable no-underscore-dangle */
import React, { useRef } from 'react';
import { Form, Card, Badge } from 'react-bootstrap';
import {
  bookingToLineItems, bookingValue, formatAmount,
  invoicedByLine, paidByLine, lineGross,
} from '../../Utils/billingoDocuments';

// Per-line status badges: already invoiced (Előleg/Számla) and/or already paid.
function lineStatusBadges(value, invoiced, paid) {
  const badges = [];
  if (invoiced >= value - 0.005 && invoiced > 0.005) {
    badges.push(<Badge key="inv" bg="info" className="ms-2">Invoiced</Badge>);
  } else if (invoiced > 0.005) {
    badges.push(<Badge key="inv" bg="secondary" className="ms-2">Part-invoiced</Badge>);
  }
  if (paid >= value - 0.005 && paid > 0.005) {
    badges.push(<Badge key="paid" bg="success" className="ms-2">Paid</Badge>);
  } else if (paid > 0.005) {
    badges.push(<Badge key="paid" bg="warning" text="dark" className="ms-2">Part-paid</Badge>);
  }
  return badges;
}

// Step 3a — granular picker. Lists each candidate booking and its
// customer-facing order_lines with per-line checkboxes, so a single document
// can cover one line, several lines, or whole bookings across MANY bookings.
// Shift-click a line to select/deselect the whole range from the last one
// clicked (across bookings, in display order). `invoicedMap` drives the
// per-booking invoiced/remaining badge; `currency` restricts the listed lines.
function BillingoLineSelector({
  bookings, selectedKeys, onChange, invoicedMap = {}, currency = null,
  documents = [], payments = [],
}) {
  // Line-level invoiced map (composite key) across the known documents.
  const invByLine = invoicedByLine(documents);
  // Anchors for shift-range selection (one for lines, one for whole bookings) +
  // the last shift state captured on click (more reliable than reading shiftKey
  // off the change event).
  const anchorRef = useRef(null);
  const bookingAnchorRef = useRef(null);
  const shiftRef = useRef(false);

  // Candidate lines, grouped by booking and flattened in display order — the
  // flat list is what a shift-range walks over.
  const groups = bookings
    .map((booking) => ({
      booking,
      candidates: bookingToLineItems(booking).filter((c) => !currency || c.currency === currency),
    }))
    .filter((g) => g.candidates.length > 0);
  const ordered = groups.flatMap((g) => g.candidates.map((c) => c.key));
  const bookingId = (b) => b._id || b.booking_reference;
  const bookingOrder = groups.map((g) => bookingId(g.booking));
  const keysForBooking = (id) => {
    const g = groups.find((x) => bookingId(x.booking) === id);
    return g ? g.candidates.map((c) => c.key) : [];
  };

  // Toggle a whole booking; shift-click selects/deselects the range of bookings
  // from the last booking header clicked.
  const handleBookingToggle = (id, checked) => {
    const next = new Set(selectedKeys);
    const anchor = bookingAnchorRef.current;
    if (shiftRef.current && anchor && bookingOrder.includes(anchor)) {
      const a = bookingOrder.indexOf(anchor);
      const b = bookingOrder.indexOf(id);
      const [lo, hi] = a < b ? [a, b] : [b, a];
      bookingOrder.slice(lo, hi + 1).forEach((bid) => keysForBooking(bid)
        .forEach((k) => (checked ? next.add(k) : next.delete(k))));
    } else {
      keysForBooking(id).forEach((k) => (checked ? next.add(k) : next.delete(k)));
    }
    bookingAnchorRef.current = id;
    onChange(next);
  };

  const handleLineToggle = (key, checked) => {
    const next = new Set(selectedKeys);
    const anchor = anchorRef.current;
    if (shiftRef.current && anchor && ordered.includes(anchor)) {
      const a = ordered.indexOf(anchor);
      const b = ordered.indexOf(key);
      const [lo, hi] = a < b ? [a, b] : [b, a];
      ordered.slice(lo, hi + 1).forEach((k) => (checked ? next.add(k) : next.delete(k)));
    } else if (checked) {
      next.add(key);
    } else {
      next.delete(key);
    }
    anchorRef.current = key;
    onChange(next);
  };

  if (groups.length === 0) {
    return <div className="text-muted small">No customer-facing lines to invoice.</div>;
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="text-muted small">
        <i className="fa fa-info-circle me-1" />
        Tip: shift-click a line or a booking to select a whole range.
      </div>
      {groups.map(({ booking, candidates }) => {
        const bookingKeys = candidates.map((c) => c.key);
        const allSelected = bookingKeys.length > 0 && bookingKeys.every((k) => selectedKeys.has(k));
        const value = bookingValue(booking);
        const agg = invoicedMap[booking._id] || { requested: 0, invoiced: 0 };
        const remaining = value.gross - agg.invoiced;
        const fullyInvoiced = agg.invoiced > 0 && remaining <= 0.005;
        // The whole booking's lines are the pool's targets, not just the
        // ones this currency filter lists — `paidByLine` takes the booking.
        const paidForBooking = paidByLine(payments, booking).byKey;

        return (
          <Card key={booking._id || booking.booking_reference}>
            <Card.Header className="d-flex justify-content-between align-items-center py-2">
              <Form.Check
                type="checkbox"
                id={`book-all-${booking._id}`}
                checked={allSelected}
                label={(
                  <span className="fw-semibold">
                    {booking.booking_reference || booking._id}
                  </span>
                )}
                onClick={(e) => { shiftRef.current = e.shiftKey; }}
                onChange={(e) => handleBookingToggle(bookingId(booking), e.target.checked)}
              />
              <span className="small">
                {fullyInvoiced ? (
                  <Badge bg="success">Fully invoiced</Badge>
                ) : (
                  <span className="text-muted">
                    invoiced
                    {' '}
                    {formatAmount(agg.invoiced, value.currency)}
                    {' / '}
                    {formatAmount(value.gross, value.currency)}
                    {agg.requested > 0 && (
                      <span className="ms-2">
                        (requested
                        {' '}
                        {formatAmount(agg.requested, value.currency)}
                        )
                      </span>
                    )}
                  </span>
                )}
              </span>
            </Card.Header>
            <Card.Body className="py-2">
              {candidates.map((line) => {
                const lineValue = lineGross(line);
                const lineInvoiced = invByLine[line.key]?.invoiced || 0;
                const linePaid = paidForBooking[line.source_key] || 0;
                return (
                  <Form.Check
                    key={line.key}
                    type="checkbox"
                    id={`line-${line.key}`}
                    checked={selectedKeys.has(line.key)}
                    onClick={(e) => { shiftRef.current = e.shiftKey; }}
                    onChange={(e) => handleLineToggle(line.key, e.target.checked)}
                    label={(
                      <span className="d-flex justify-content-between align-items-center" style={{ minWidth: 320 }}>
                        <span>
                          {line.comment || line.description}
                          {lineStatusBadges(lineValue, lineInvoiced, linePaid)}
                        </span>
                        <span className="text-muted ms-3">
                          {formatAmount(line.unit_price, line.currency)}
                        </span>
                      </span>
                    )}
                  />
                );
              })}
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
}

export default BillingoLineSelector;
